import React from 'react';
import type { CSSProperties, ReactNode, SelectHTMLAttributes, ChangeEventHandler } from 'react';
import useStyles from '../../theme/useStyles';

// options: ['A', 'B'] or [{ value, label }]
/** Either a plain string, or an explicit value/label pair. */
export type SelectOption = string | { value: string; label: ReactNode };

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'style' | 'onChange' | 'value'> {
  label?: ReactNode;
  name?: string;
  value?: string | number;
  onChange?: ChangeEventHandler<HTMLSelectElement>;
  options?: readonly SelectOption[];
  /** Empty string disables the blank first option. */
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: ReactNode;
  hint?: ReactNode;
  fullWidth?: boolean;
  style?: CSSProperties;
  containerStyle?: CSSProperties;
  isDarkMode?: boolean;
}

const Select = ({
  label,
  name,
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  required = false,
  disabled = false,
  error,
  fullWidth = false,
  style,
  containerStyle,
  isDarkMode,
  ...rest
}: SelectProps) => {
  const s = useStyles(isDarkMode);

  // Narrow on the string case: TS cannot refine the false branch of
  // `typeof opt === 'object' && opt !== null` back down to `string`.
  const normalized: Array<{ value: string; label: ReactNode }> = options.map((opt) =>
    (typeof opt === 'string' ? { value: opt, label: opt } : opt)
  );

  // A native option popup does not reliably inherit the select's colours, so
  // in dark mode the open list rendered as light text on a white sheet
  // (CHM-117). Each option carries the field's own colours instead.
  const optionStyle = { backgroundColor: s.t.bgInput, color: s.t.textMain };

  return (
    <div style={{ ...s.inputGroup, ...(fullWidth ? s.fullWidth : {}), ...containerStyle }}>
      {label && (
        <label htmlFor={name} style={s.label}>
          {label}
          {required && <span style={{ color: s.t.error, marginLeft: '4px' }}>*</span>}
        </label>
      )}
      <select
        id={name} name={name} value={value ?? ''} onChange={onChange}
        required={required} disabled={disabled}
        style={{
          ...s.input,
          ...(error ? { borderColor: s.t.error } : {}),
          ...(disabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
          ...style
        }}
        {...rest}
      >
        {placeholder && <option value="" style={optionStyle}>{placeholder}</option>}
        {normalized.map((opt) => (
          <option key={opt.value} value={opt.value} style={optionStyle}>{opt.label}</option>
        ))}
      </select>
      {error && <span style={{ fontSize: s.t.fontSize.sm, color: s.t.error, marginTop: '4px' }}>{error}</span>}
    </div>
  );
};

export default Select;
